import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function MessageAdmin() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    api.get("/messages").then(({ data }) => setMsgs(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/messages", { body: text.trim() });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (e) { toast.error("Could not send message"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="message-admin-panel">
      <h3 className="font-display font-bold flex items-center gap-2 mb-1"><MessageSquare className="w-4 h-4 text-primary-c" /> Message the team</h3>
      <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>Questions or feedback? Send us a note — we'll reply right here.</p>
      <div className="rounded-xl p-3 mb-3 space-y-2 max-h-72 overflow-y-auto" style={{ background: "var(--bg)" }} data-testid="message-thread">
        {msgs.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: "var(--text-2)" }}>No messages yet. Say hello 👋</p>
        ) : msgs.map((m) => (
          <div key={m.message_id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "user" ? "btn-glow" : "glass"}`}
              style={m.sender === "user" ? {} : { color: "var(--text)" }} data-testid={`msg-${m.sender}`}>
              {m.sender === "admin" && <span className="block text-[10px] font-bold text-primary-c mb-0.5">PointControl Team</span>}
              <span className="whitespace-pre-wrap">{m.body}</span>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="Type a message…"
          className="flex-1 rounded-full bg-surface glow-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
        <button data-testid="message-send" onClick={send} disabled={busy || !text.trim()}
          className="btn-glow rounded-full px-4 flex items-center gap-2 font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
